// frontend/src/pages/SalesProgram.js
// Strategic Sales Performance Management Operating Procedure.
// Reference document: Patch's guide (May 2026). Tracks qualitative
// reflection + quantitative Acquisition Revenue / Renewal results,
// organised around quarterly Group Alignment Sessions (GAS).
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Badge, SectionTitle } from '../components/UI';
import PageHeader from '../components/PageHeader';

/* ── reusable mini-components (mirroring ProgramDetails) ──────────────── */
const TH = ({ children, style }) => (
  <th style={{
    padding: '9px 14px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase',
    letterSpacing: '0.5px', color: 'var(--text-3)', borderBottom: '0.5px solid var(--border)',
    fontWeight: 500, background: 'var(--gray-50)', ...style,
  }}>{children}</th>
);
const TD = ({ children, style }) => (
  <td style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-1)', borderBottom: '0.5px solid var(--border)', ...style }}>
    {children}
  </td>
);
const Table = ({ children }) => (
  <Card style={{ padding: 0, overflow: 'hidden', marginTop: 10, marginBottom: 20 }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>{children}</table>
  </Card>
);
const P = ({ children, style }) => (
  <p style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.7, margin: '0 0 14px', ...style }}>{children}</p>
);
const SectionNum = ({ num, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '32px 0 14px' }}>
    <div style={{
      width: 28, height: 28, borderRadius: '50%', background: 'var(--navy-600)', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 500, flexShrink: 0,
    }}>{num}</div>
    <h2 style={{ fontSize: 17, fontWeight: 500, margin: 0 }}>{title}</h2>
  </div>
);

/* ── static data derived from the Operating Procedure ────────────────── */
const PROGRAM_SUMMARY = [
  ['Role covered',         'EMC — Energy Management Consultant'],
  ['Evaluation Period',    'Quarterly — Australian FY (Jul–Jun)'],
  ['Financial Year',       '1 July – 30 June'],
  ['Review Structure',     'Group Alignment Sessions (GAS) in the last fortnight of each quarter, plus optional follow-up meetings in the last week'],
  ['Stakeholders',         'EMC + James + Ewen + Patch (facilitator). Additional stakeholders invited as needed.'],
  ['Qualitative Inputs',   'Reflections · Motivation · Action Plan · Weekly journaling'],
  ['Quantitative KPIs',    'Acquisition Revenue (target $/month) · Renewal Conversion Rate (target 90%)'],
  ['Intent',               'Collaborative, motivating and inclusive — not a formal performance improvement or disciplinary process.'],
];

const FY_SCHEDULE = [
  ['May 2026',                     'Review draft FY Sales Strategy with Patch in preparation for a 10-minute individual presentation at the Sales Conference.'],
  ['Early June 2026',              'Sales Conference in Manly.'],
  ['Last fortnight of June 2026',  'GAS meetings to plan July, August and September.'],
  ['Jul – Sep 2026',               'Weekly EMC journaling on progress, reflections and key learnings.'],
  ['Last fortnight of Sep 2026',   'GAS meetings to review Q1 results and plan October, November and December.'],
];

const WHAT_TO_TRACK = [
  ['Reflections',                  'Understand your own perspective (and let it evolve); promote self-learning; build accountability.'],
  ['The future',                   'Understand your own motivation levels and what needs to be true for you to hit your targets.'],
  ['Acquisition Revenue & Renewal KPIs', 'Stay aware of your own quantitative performance throughout the quarter.'],
  ['Action Plan',                  'Know what activity, and what level of activity, drives positive sales results — so we can coach to it.'],
];

const ACTION_PLAN_EXAMPLE = [
  ['Targets',                  'Acquisition Revenue — $100,000 per month · Renew 90%', '…', '…'],
  ['Pipeline Management',      'What needs to happen to my pipe to reach target?',       '…', '…'],
  ['Monthly Activity Commitment',
    'I will meet with …\nI will call 50 businesses in …\nI will research …\nI will visit …\nI will build a campaign …',
    '…', '…'],
  ['Support',                  'What support do I need to do these things. Is this level of support feasible?', '…', '…'],
];

const GAS_BENEFITS = [
  'Multiple perspectives — others may see risks, opportunities, or approaches you haven\u2019t considered.',
  'Structured space to step back and reflect on what went well, what didn\u2019t, and why. Builds self-awareness and professional judgement over time.',
  'Normalises challenges, reduces isolation, and provides support.',
  'Reinforces professional standards — encourages ethical practice, consistency, and responsibility for decisions.',
];

const KPI_FRAMEWORK = [
  ['Revenue',           'Acquisition Revenue',              'Monthly revenue delivered vs target (baseline $100k/month). Averaged across the quarter.',                                                              '30%'],
  ['Revenue',           'Renewal Conversion Rate',          'Percentage of eligible renewals converted during the quarter. Baseline target 90%.',                                                                   '20%'],
  ['Activity',          'Monthly Activity Commitment',      'Adherence to the committed monthly plan — meetings, calls, research, visits, campaigns.',                                                              '15%'],
  ['Pipeline',          'Pipeline Management',              'Health and progression of the sales pipeline — what needs to happen to the pipe to reach target.',                                                     '10%'],
  ['Execution',         'Action Plan Execution',            'Quality of and adherence to the documented monthly action plan — do the stated activities actually drive results?',                                   '10%'],
  ['Self-awareness',    'Weekly Journaling & Reflection',   'Discipline of weekly journaling on progress, reflections and key learnings across the quarter.',                                                      '10%'],
  ['Collaboration',     'GAS Participation',                'Active engagement in the quarterly Group Alignment Session.',                                                                                         '5%'],
];

/* ── page ────────────────────────────────────────────────────────────── */
export default function SalesProgram() {
  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)', maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ marginBottom: 22 }}>
        <PageHeader moduleKey="program-guide-sales"
          subtitle="Strategic Sales Performance Management · Qualitative reflection plus quantitative results, built around quarterly Group Alignment Sessions." />
      </div>

      {/* Quick links to the three Sales-specific tools */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12, marginBottom: 24 }}>
        <Link to="/sales/action-plans" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Card style={{ cursor: 'pointer', borderLeft: '3px solid var(--navy-600)' }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>📋 Action plans</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, lineHeight: 1.5 }}>Monthly targets, pipeline, activity and support — per EMC per cycle.</div>
          </Card>
        </Link>
        <Link to="/sales/journal" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Card style={{ cursor: 'pointer', borderLeft: '3px solid var(--blue-500)' }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>📓 Weekly journal</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, lineHeight: 1.5 }}>Progress, reflections, and key learnings every week.</div>
          </Card>
        </Link>
        <Link to="/sales/gas" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Card style={{ cursor: 'pointer', borderLeft: '3px solid var(--amber-500)' }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>👥 GAS sessions</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, lineHeight: 1.5 }}>Quarterly Group Alignment Sessions with multiple stakeholders.</div>
          </Card>
        </Link>
      </div>

      <SectionNum num={1} title="Program summary" />
      <Table>
        <thead><tr><TH>Item</TH><TH>Description</TH></tr></thead>
        <tbody>
          {PROGRAM_SUMMARY.map(([k, v]) => (
            <tr key={k}><TD style={{ fontWeight: 500, width: 220 }}>{k}</TD><TD>{v}</TD></tr>
          ))}
        </tbody>
      </Table>

      <SectionNum num={2} title="What every EMC tracks" />
      <P>
        Team members are required to note down, document and track the following throughout the quarter. These become the
        inputs to the GAS and to your quarterly scorecard.
      </P>
      <Table>
        <thead><tr><TH>Item</TH><TH>Why it matters</TH></tr></thead>
        <tbody>
          {WHAT_TO_TRACK.map(([k, v]) => (
            <tr key={k}><TD style={{ fontWeight: 500, width: 260 }}>{k}</TD><TD>{v}</TD></tr>
          ))}
        </tbody>
      </Table>

      <SectionNum num={3} title="Action plan format" />
      <P>
        We need to know <strong>what activity</strong> and <strong>what level of activity</strong> drives positive sales results — so we can talk to and coach to it.
        This is the preferred template; team members are welcome to come back with something that works better for them.
      </P>
      <Table>
        <thead>
          <tr>
            <TH>Month</TH><TH>July</TH><TH>August</TH><TH>September</TH>
          </tr>
        </thead>
        <tbody>
          {ACTION_PLAN_EXAMPLE.map(([row, jul, aug, sep]) => (
            <tr key={row}>
              <TD style={{ fontWeight: 500, width: 210 }}>{row}</TD>
              <TD style={{ whiteSpace: 'pre-line' }}>{jul}</TD>
              <TD>{aug}</TD>
              <TD>{sep}</TD>
            </tr>
          ))}
        </tbody>
      </Table>

      <SectionNum num={4} title="Group Alignment Sessions (GAS)" />
      <P>
        GAS are held in the <strong>last fortnight of each quarter</strong>, with follow-ups in the last week if needed.
        The GAS is where experience, reflection, and support come together to make individuals — and the team — better.
      </P>
      <P>
        Participants: <strong>the EMC, James, Ewen, and Patch</strong> (Patch facilitates). Other stakeholders may be invited from time to time.
        These meetings are <em>not</em> formal performance-improvement or disciplinary discussions — they're collaborative, motivating, inclusive.
      </P>
      <Card>
        <SectionTitle>Why we do them</SectionTitle>
        <ul style={{ margin: '10px 0 0 18px', padding: 0, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.7 }}>
          {GAS_BENEFITS.map(b => <li key={b} style={{ marginBottom: 6 }}>{b}</li>)}
        </ul>
      </Card>

      <SectionNum num={5} title="KPI framework" />
      <P>
        Quarterly scorecard weights sum to 100%. Weights are suggested defaults — admins can reweight per the team's direction in the KPI templates tab.
      </P>
      <Table>
        <thead>
          <tr>
            <TH style={{ width: 140 }}>Category</TH>
            <TH style={{ width: 240 }}>KPI</TH>
            <TH>Description</TH>
            <TH style={{ width: 70, textAlign: 'right' }}>Weight</TH>
          </tr>
        </thead>
        <tbody>
          {KPI_FRAMEWORK.map(([cat, kpi, desc, w]) => (
            <tr key={kpi}>
              <TD><Badge color={cat === 'Revenue' ? 'green' : cat === 'Activity' ? 'amber' : cat === 'Pipeline' ? 'blue' : 'gray'}>{cat}</Badge></TD>
              <TD style={{ fontWeight: 500 }}>{kpi}</TD>
              <TD style={{ color: 'var(--text-2)' }}>{desc}</TD>
              <TD style={{ textAlign: 'right', fontWeight: 500 }}>{w}</TD>
            </tr>
          ))}
        </tbody>
      </Table>

      <SectionNum num={6} title="Year-at-a-glance (current FY)" />
      <Table>
        <thead><tr><TH style={{ width: 240 }}>When</TH><TH>What happens</TH></tr></thead>
        <tbody>
          {FY_SCHEDULE.map(([when, what]) => (
            <tr key={when}><TD style={{ fontWeight: 500 }}>{when}</TD><TD>{what}</TD></tr>
          ))}
        </tbody>
      </Table>

      <div style={{ marginTop: 32, padding: '12px 16px', background: 'var(--gray-50)', borderRadius: 8, fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6 }}>
        <SectionTitle>Source document</SectionTitle>
        <div style={{ marginTop: 6 }}>
          Strategic Sales Performance Management Operating Procedure — Patch, May 2026.
          Any changes to the process here should be mirrored in the KPI templates and review cycles under this program.
        </div>
      </div>
    </div>
  );
}
